'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from './components/CustomerDashboardLayout';

interface CustomerProfile {
  full_name: string;
  email: string;
  phone: string | null;
  loyalty_points: number;
  lifetime_points: number;
}

interface RecentOrder {
  id: string;
  order_number: string;
  status: string;
  total_amount: number;
  created_at: string;
  items_summary: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-blue-100 text-blue-700',
  processing: 'bg-purple-100 text-purple-700',
  shipped: 'bg-indigo-100 text-indigo-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

export default function CustomerDashboardPage() {
  const router = useRouter();
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [orderCount, setOrderCount] = useState(0);

  useEffect(() => {
    async function loadData() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { router.push('/customer-login'); return; }

      const { data: profileData } = await supabase
        .from('user_profiles')
        .select('full_name, email, phone, loyalty_points, lifetime_points')
        .eq('id', user.id)
        .maybeSingle();

      if (profileData) setProfile(profileData);

      const { data: orders, count } = await supabase
        .from('platform_orders')
        .select('id, order_number, status, total_amount, created_at, items_summary', { count: 'exact' })
        .eq('customer_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5);

      setRecentOrders(orders || []);
      setOrderCount(count || 0);
      setLoading(false);
    }
    loadData();
  }, [router]);

  if (loading) {
    return (
      <CustomerDashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
        </div>
      </CustomerDashboardLayout>
    );
  }

  const firstName = profile?.full_name?.split(' ')[0] || 'Customer';

  return (
    <CustomerDashboardLayout>
      <div className="space-y-6">
        {/* Welcome */}
        <div className="bg-gradient-to-r from-primary to-green-600 rounded-2xl p-6 text-white">
          <p className="text-sm text-white/70 mb-1">Welcome back,</p>
          <h1 className="text-2xl font-700">{firstName} 👋</h1>
          <p className="text-sm text-white/70 mt-1">Here&apos;s what&apos;s happening with your account</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Orders', value: orderCount, icon: 'ShoppingBagIcon', cls: 'bg-blue-50 text-blue-600' },
            { label: 'Loyalty Points', value: (profile?.loyalty_points || 0).toLocaleString('en-IN'), icon: 'StarIcon', cls: 'bg-amber-50 text-amber-600' },
            { label: 'Lifetime Points', value: (profile?.lifetime_points || 0).toLocaleString('en-IN'), icon: 'TrophyIcon', cls: 'bg-purple-50 text-purple-600' },
            { label: 'Active Orders', value: recentOrders.filter(o => ['pending','confirmed','processing','shipped'].includes(o.status)).length, icon: 'TruckIcon', cls: 'bg-green-50 text-green-600' },
          ].map(card => (
            <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-5">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${card.cls}`}>
                <Icon name={card.icon as never} size={20} />
              </div>
              <p className="text-xl font-700 text-slate-900">{card.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Shop Now', href: '/shop', icon: 'ShoppingBagIcon' },
            { label: 'My Orders', href: '/customer-dashboard/orders', icon: 'ClipboardDocumentListIcon' },
            { label: 'Payments', href: '/customer-dashboard/payments', icon: 'CreditCardIcon' },
            { label: 'Returns', href: '/customer-dashboard/returns', icon: 'ArrowUturnLeftIcon' },
            { label: 'Reviews', href: '/customer-dashboard/reviews', icon: 'StarIcon' },
            { label: 'Wishlist', href: '/customer-dashboard/wishlist', icon: 'HeartIcon' },
            { label: 'Notifications', href: '/customer-dashboard/notifications', icon: 'BellIcon' },
            { label: 'Support', href: '/customer-dashboard/support', icon: 'ChatBubbleLeftRightIcon' },
          ].map(action => (
            <Link
              key={action.label}
              href={action.href}
              className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
            >
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Icon name={action.icon as never} size={20} className="text-primary" />
              </div>
              <span className="text-xs font-600 text-slate-700">{action.label}</span>
            </Link>
          ))}
        </div>

        {/* Recent Orders */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-700 text-slate-900">Recent Orders</h2>
            <Link href="/customer-dashboard/orders" className="text-xs text-primary font-600 hover:underline">View all</Link>
          </div>
          {recentOrders.length === 0 ? (
            <div className="py-12 text-center">
              <Icon name="ShoppingBagIcon" size={32} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-400">No orders yet</p>
              <p className="text-xs text-slate-400 mt-1">Start shopping on Bozaat!</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentOrders.map(order => (
                <div key={order.id} className="px-5 py-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-600 text-slate-900">#{order.order_number}</p>
                    <p className="text-xs text-slate-400 truncate mt-0.5">{order.items_summary || 'Order items'}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs font-500 px-2.5 py-1 rounded-full ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                    <span className="text-sm font-600 text-slate-900">₹{order.total_amount?.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CustomerDashboardLayout>
  );
}
